import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { Bot, BotStyle, BotLanguage, BotVoiceType } from 'interfaces/db';
import { getServicesManager } from 'services';
import {
  setSessionBot,
  setSessionBotError,
  addPracticeBot,
  setIsConstructing,
  setSessionBotLoaded,
} from './session-bot-slices';
import uuid from 'react-uuid';
import { SessionDataResult } from 'interfaces/sessions';

export const usePracticeBot = (sessionId: string) => {
  const dispatch = useDispatch();
  const sessionBotState = useSelector((state: RootState) => 
    state.practiceBot.sessionBots[sessionId] || {
      bot: null,
      isLoaded: false,
      error: null,
      isConstructing: false,
    }
  );

  const { bot, isLoaded, isConstructing, error } = sessionBotState;
  const emailValue = useSelector((state: RootState) => state.emailValueProps.value)
  const user = useSelector((state: RootState) => state.user.value)

  const fetchPracticeBot = useCallback(async () => {
    if (isLoaded) return
    try {
      const existingBots = await getServicesManager().getProspectBots(sessionId);
      if (existingBots && existingBots.length > 0) {
        dispatch(setSessionBot({ sessionId, bot: existingBots[0] }));
      } else {
        dispatch(setSessionBot({ sessionId, bot: null }));
      }
    } catch (err) {
    } finally {
      dispatch(setSessionBotLoaded({ sessionId, isLoaded: true }));
    }
  }, [dispatch, sessionId]);

  useEffect(() => {
    fetchPracticeBot();
  }, [sessionId])

  const getVoice = (is_male: boolean) => {
    const voices = getVoices(is_male)
    return voices[Math.floor(Math.random() * voices.length)]
}

  const getVoices = (is_male: boolean) => {
    if (is_male) {
        return [BotVoiceType.MIDDLE_AGED_MAN, BotVoiceType.AUTHORITATIVE_MAN, BotVoiceType.CALM_MAN, BotVoiceType.YOUNG_MAN, BotVoiceType.OLD_MAN]
    } else {
        return [BotVoiceType.MIDDLE_AGED_WOMAN, BotVoiceType.AUTHORITATIVE_WOMAN, BotVoiceType.CALM_WOMAN, BotVoiceType.YOUNG_WOMAN, BotVoiceType.OLD_WOMAN]
    }
}

  const createPracticeBot = useCallback(async (sessionData: SessionDataResult) => {
    try {
      if (!user) return
      if (isConstructing) return
      dispatch(setIsConstructing({ sessionId, isConstructing: true }));
      // Replace with your actual service call to create a bot
      const botInformation = await getServicesManager().generateBot([sessionData], emailValue ? emailValue[0] : null);
      if (!botInformation) {
        throw new Error('Failed to create practice bot');
      }
      const name = sessionData.prospect_info?.prospect_name ?? 'Prospect';
      const firstName = name.split(' ')[0];
      const newBot = await getServicesManager().addBot({
          'bot_id': uuid(),
          'team_id': user.team_id,
          'meta_description': botInformation.meta_description,
          'background_instruction': botInformation.background,
          'is_global': false,
          'bot_language': BotLanguage.ENGLISH,
          'bot_style': BotStyle.COLD_CALL,
          'objections_instruction': botInformation.objections,
          'problem_instruction': botInformation.problems,
          'custom_instructions': botInformation.custom_instructions,
          'is_active': true,
          'name': firstName,
          'updated_at': new Date().toISOString(),
          'tags': [],
          'user_id': user.user_id,
          'xref_profile_url': '',
          'xref_prospect_id': sessionData.session.session_id,
          'voice': getVoice(botInformation.is_male)
      })
      if (!newBot) {
        dispatch(setSessionBotError({ sessionId, error: 'Failed to create practice bot' }));
      } else {
        dispatch(addPracticeBot({ sessionId, bot: newBot }));
        return newBot;
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error creating practice bot';
      dispatch(setSessionBotError({ sessionId, error: errorMessage }));
      console.error('Error creating practice bot:', err);
      return null;
    } finally {
      dispatch(setIsConstructing({ sessionId, isConstructing: false }));
    }
  }, [dispatch, sessionId]);

  return {
    bot,
    isLoaded,
    isConstructing,
    error,
    fetchPracticeBot,
    createPracticeBot,
  };
}; 